import { ChangeDetectorRef, Directive, effect, inject, OnDestroy, OnInit } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { LatLng, LatLngBounds, latLng, latLngBounds } from 'leaflet';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';

import { ConnectionService } from '../../services/connection.service';
import { StorageService } from '../../services/storage/storage.service';
import { MapStatusService } from '../../map/services/map-status.service';
import { OfflineModeService } from '../../services/offline-mode.service';
import { SelectionService } from '../../services/selection.service';
import { TransformationService } from '../../map/services/transformation.service';
import { SplashScreenService } from '../../services/splash-screen.service';
import { ConfirmService } from '../../services/confirm.service';
import { ProjectPointsService } from '../../map/project/project-points.service';
import { DataProvider } from '../interfaces/data-provider.interface';
import { FilterProvider } from '../interfaces/filter-provider.interface';
import { NavbarProvider } from '../interfaces/navbar-provider.interface';
import { ProjectProvider } from '../interfaces/project-provider.interface';
import { AuthProvider } from '../interfaces/auth-provider.interface';
import { MapMarkerHandler, MapMarker, MAP_MARKER_HANDLER } from '../../map/handlers/map-marker.handler';
import { DATA_PROVIDER, FILTER_PROVIDER, NAVBAR_PROVIDER, PROJECT_PROVIDER, AUTH_PROVIDER, FILTER_COMPONENT } from '../tokens';
import { BaseDocument } from '../types';
import { ListFilterMode } from './base-list-panel.component';

export { ListFilterMode };

/**
 * Abstrakte Basisklasse für Seiten mit Karte + Liste Layout.
 *
 * Stellt generische Infrastruktur bereit:
 * - Datenlade-Orchestrierung mit Debouncing und Splash-Screen
 * - MapMarker-Caching mit MAP_MARKER_HANDLER
 * - Bounds-Management (Kartenausschnitt → boundsFilter)
 * - Filter-Orchestrierung (Quick-Filter mit Bounds-/Auswahl-Modus)
 * - Lifecycle-Management (Subscriptions, Destroy)
 * - Sortier-Cache für Liste
 *
 * Subklassen implementieren:
 * - initPoints() — Domänenspezifisches Laden der Daten
 * - matchesSearch() — Domänenspezifische Textsuche
 * - getPointLatLng() — Koordinatenextraktion aus Dokumenten
 * - initButtons() — Command-Subscriptions
 * - getSubscribedCommands() — Liste der abonnierten Commands (für Auto-Unsubscribe)
 */
@Directive()
export abstract class BaseMapListComponent<T extends BaseDocument = BaseDocument> implements OnInit, OnDestroy {
	// --- Injected Services ---
	protected modalService = inject(NgbModal);
	protected markerHandler = inject(MAP_MARKER_HANDLER) as MapMarkerHandler<T>;
	protected splash = inject(SplashScreenService);
	protected cd = inject(ChangeDetectorRef);
	protected cs = inject(ConnectionService);
	protected storage = inject(StorageService);
	protected mapStatusService = inject(MapStatusService);
	protected oms = inject(OfflineModeService);
	protected selectionService = inject(SelectionService);
	protected ts = inject(TransformationService);
	protected msg = inject(ToastrService);
	protected confirmService = inject(ConfirmService);
	protected pps = inject(ProjectPointsService);
	protected dataProvider = inject(DATA_PROVIDER) as unknown as DataProvider<T>;
	protected filterProvider = inject(FILTER_PROVIDER) as FilterProvider;
	protected navbarProvider = inject(NAVBAR_PROVIDER) as NavbarProvider;
	protected projectProvider = inject(PROJECT_PROVIDER) as ProjectProvider;
	protected authProvider = inject(AUTH_PROVIDER) as AuthProvider;
	private filterComponentType = inject(FILTER_COMPONENT, { optional: true });

	// --- State ---
	bps: T[] = [];
	boundsFilter: boolean[] = [];
	boundsFilterActive = true;
	currentBounds?: LatLngBounds;
	listFilterMode: ListFilterMode = 'map';
	protected destroy$ = new Subject<void>();
	workOffline = false;

	searchText = '';
	filterNames: string[] = [];
	currentFilterName: string | null = null;
	mapFilterStale = false;
	private lastFilteredBounds?: LatLngBounds;

	points?: LatLng[];
	pointsFilter: boolean[] = [];
	haveFilter = false;
	havePoints = false;
	filteredCount = 0;

	listSortMode: 'none' | 'selected' | 'filtered' = 'none';
	sortedBpsCache: { bp: T; originalIndex: number }[] = [];
	private sortCacheValid = false;

	// MapMarker-Cache
	private _mapMarkersCache?: MapMarker[];
	private _mapMarkersCacheValid = false;

	// Debounce
	private initPointsDebounceTimer?: ReturnType<typeof setTimeout>;
	private readonly INIT_POINTS_DEBOUNCE_MS = 100;
	protected initPointsVersion = 0;

	// --- Abstract Methods ---

	/** Domänenspezifisches Laden der Punkte aus der Datenbank */
	abstract initPoints(): Promise<void>;

	/** Domänenspezifische Textsuche über Punkt-Felder */
	abstract matchesSearch(bp: T): boolean;

	/** Koordinaten aus einem Punkt-Dokument extrahieren */
	abstract getPointLatLng(bp: T): LatLng | null;

	/** Command-Subscriptions einrichten (Navbar-Buttons) */
	abstract initButtons(): void;

	/** Liste der abonnierten Command-Nummern (für Auto-Unsubscribe in ngOnDestroy) */
	abstract getSubscribedCommands(): number[];

	// --- Constructor ---

	constructor() {
		this.setupBaseSubscriptions();
	}

	private setupBaseSubscriptions(): void {
		// Reagiere auf Projektwechsel
		effect(() => {
			const project = this.projectProvider.project();
			if (project && this.dataProvider.isInitialized) {
				this.initButtons();
			}
		});

		// Selection-Änderungen → Change Detection
		this.selectionService.selectionChanged.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.cd.detectChanges();
		});

		// Connection-Status
		const onlineStateMessage = (isOnline: boolean) => {
			this.workOffline = !isOnline;
		};
		this.cs.monitor().pipe(takeUntil(this.destroy$)).subscribe(onlineStateMessage);
		onlineStateMessage(navigator.onLine);

		// Data-Events → Punkte neu laden
		this.dataProvider.checkedOut.pipe(takeUntil(this.destroy$)).subscribe(() => this.initPointsDebounced());
		this.dataProvider.checkedIn.pipe(takeUntil(this.destroy$)).subscribe(() => this.initPointsDebounced());
		this.dataProvider.discarded.pipe(takeUntil(this.destroy$)).subscribe(() => this.initPointsDebounced());
		this.dataProvider.pointsRefreshRequested.pipe(takeUntil(this.destroy$)).subscribe(() => this.initPointsDebounced());

		// Bei neuem Punkt: Zur Liste hinzufügen
		this.dataProvider.pointSaved.pipe(takeUntil(this.destroy$)).subscribe((bp: T) => {
			if (bp._id && !this.bps.some(p => p._id === bp._id)) {
				this.bps.push(bp);
				this.bps = [...this.bps];
				this.invalidateMapMarkersCache();
				this.invalidateSortCache();
				this.rebuildSortCache();
			}
		});

		// Filter-Events
		this.filterProvider.apply.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.onApplyFilter();
			this.cd.detectChanges();
		});
		this.filterProvider.filtersUpdated.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.onApplyFilter();
			this.loadFilterNames();
			this.cd.detectChanges();
		});

		// Gespeicherte Einstellungen laden
		this.loadFilterNames();
		this.currentFilterName = this.storage.getSync('currentFilter');
		this.searchText = this.storage.getSync('searchText') || '';
		const savedListFilterMode = this.storage.getSync('listFilterMode') as ListFilterMode;
		if (savedListFilterMode) {
			this.listFilterMode = savedListFilterMode;
			this.boundsFilterActive = (savedListFilterMode === 'map');
		}

		// Kartenausschnitt → Bounds-Filter
		this.mapStatusService.globalMapState().pipe(takeUntil(this.destroy$)).subscribe(state => {
			if (state.bounds) {
				const b = state.bounds as any;
				if (b._southWest && b._northEast) {
					this.currentBounds = latLngBounds(
						latLng(b._southWest.lat, b._southWest.lng),
						latLng(b._northEast.lat, b._northEast.lng)
					);
				} else if (typeof state.bounds.contains === 'function') {
					this.currentBounds = state.bounds;
				}
				// Async um NG0100 zu vermeiden
				setTimeout(() => {
					this.updateBoundsFilter();
					if (this.listFilterMode === 'map' && this.currentFilterName && this.lastFilteredBounds) {
						if (!this.currentBounds?.equals(this.lastFilteredBounds)) {
							this.mapFilterStale = true;
						}
					}
				});
			}
		});
	}

	// --- Lifecycle ---

	ngOnInit(): void {
		const currentFilter = this.storage.getSync('currentFilter');

		const updateFn = () => {
			this.haveFilter = this.projectProvider.canI('use', 'filter');
			this.havePoints = this.projectProvider.canI('read', 'points');
			this.initButtons();
			this.initPointsDebounced();
		};
		if (this.dataProvider.isInitialized) {
			updateFn();
		}
		this.dataProvider.initialized.pipe(takeUntil(this.destroy$)).subscribe(() => updateFn());
		this.authProvider.authChanged.pipe(takeUntil(this.destroy$)).subscribe(() => updateFn());

		// Filter nach Initialisierung anwenden
		if (currentFilter) {
			if (this.dataProvider.isInitialized) {
				this.applyQuickFilter(currentFilter);
			} else {
				this.dataProvider.initialized.pipe(takeUntil(this.destroy$)).subscribe(() => {
					this.applyQuickFilter(currentFilter);
				});
			}
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
		// Auto-Unsubscribe aller abonnierten Commands
		for (const cmd of this.getSubscribedCommands()) {
			this.navbarProvider.unsubscribe(cmd);
		}
		if (this.initPointsDebounceTimer) {
			clearTimeout(this.initPointsDebounceTimer);
		}
	}

	// --- MapMarker-Cache ---

	get mapMarkers(): MapMarker[] {
		if (this._mapMarkersCacheValid && this._mapMarkersCache) {
			return this._mapMarkersCache;
		}
		this._mapMarkersCache = this.bps
			.filter(bp => this.markerHandler.hasValidCoordinates(bp))
			.map(bp => this.markerHandler.toMapMarker(bp));
		this._mapMarkersCacheValid = true;
		return this._mapMarkersCache;
	}

	invalidateMapMarkersCache(): void {
		this._mapMarkersCacheValid = false;
	}

	// --- Debounced initPoints ---

	initPointsDebounced(): void {
		if (this.initPointsDebounceTimer) {
			clearTimeout(this.initPointsDebounceTimer);
		}
		this.initPointsDebounceTimer = setTimeout(() => {
			this.initPoints();
		}, this.INIT_POINTS_DEBOUNCE_MS);
	}

	// --- Bounds-Filter ---

	updateBoundsFilter(): void {
		if (!this.currentBounds || !this.bps || this.bps.length === 0) return;
		this.boundsFilter = this.bps.map(bp => {
			const latLngPoint = this.getPointLatLng(bp);
			return latLngPoint ? this.currentBounds!.contains(latLngPoint) : true;
		});
	}

	// --- Sichtbarkeit ---

	isVisibleInList(i: number): boolean {
		if (!this.bps || !this.bps[i]) return false;
		const bp = this.bps[i];

		// 1. Filter (aus Filtermenü)
		if (this.currentFilterName) {
			if (!this.filterProvider.filteredDocIds.has(bp._id)) return false;
		} else {
			switch (this.listFilterMode) {
				case 'map':
					if (!(this.boundsFilter[i] ?? true)) return false;
					break;
				case 'selection':
					if (!this.selectionService.isSelected(bp._id)) return false;
					break;
				case 'all':
				default:
					break;
			}
		}

		// 2. Schnellsuche
		if (!this.matchesSearch(bp)) return false;
		return true;
	}

	getVisibleCount(): number {
		if (!this.bps) return 0;
		return this.bps.filter((_, i) => this.isVisibleInList(i)).length;
	}

	getMapCount(): number {
		if (!this.boundsFilter) return 0;
		return this.boundsFilter.filter(b => b).length;
	}

	getFilteredCount(): number {
		return this.filteredCount;
	}

	// --- Suchfeld ---

	onSearchTextChange(text: string): void {
		this.searchText = text;
		this.storage.setSync('searchText', text);
	}

	clearSearch(): void {
		this.searchText = '';
		this.storage.removeSync('searchText');
	}

	// --- Filter ---

	loadFilterNames(): void {
		this.filterProvider.list_keys().then((names: string[]) => {
			this.filterNames = names;
		});
	}

	onApplyFilter(): void {
		const pointsFilter: boolean[] = [];
		this.bps.forEach((bp: T) => {
			pointsFilter.push(this.filterProvider.filteredDocIds.has(bp._id));
		});
		this.pointsFilter = pointsFilter;
		this.filteredCount = pointsFilter.filter(f => f).length;
		this.invalidateSortCache();
		this.rebuildSortCache();
	}

	onFilterApplied(filterName: string): void {
		this.currentFilterName = filterName;
		this.onApplyFilter();
		this.cd.detectChanges();
	}

	applyQuickFilter(name: string): void {
		this.currentFilterName = name;
		this.storage.setSync('currentFilter', name);

		this.filterProvider.get(name).then((conditions) => {
			if (conditions) {
				if (this.listFilterMode === 'map' && this.currentBounds) {
					const sw = this.currentBounds.getSouthWest();
					const ne = this.currentBounds.getNorthEast();
					const minP = this.ts.toEPSG5650(sw);
					const maxP = this.ts.toEPSG5650(ne);
					const bnds = [{ l: minP, u: maxP }];
					this.filterProvider.storeViews(name, conditions, true, bnds, () => {
						this.filterProvider.applyFilter(name);
						this.mapFilterStale = false;
						this.lastFilteredBounds = this.currentBounds;
						this.cd.detectChanges();
					});
				} else if (this.listFilterMode === 'selection') {
					this.filterProvider.storeViews(name, conditions, false, [], () => {
						this.filterProvider.applyFilter(name).then(() => {
							const selection = this.selectionService.getSelection();
							const filtered = new Set<string>();
							this.filterProvider.filteredDocIds.forEach(id => {
								if (selection.has(id)) {
									filtered.add(id);
								}
							});
							this.filterProvider.filteredDocIds.clear();
							filtered.forEach(id => this.filterProvider.filteredDocIds.add(id));
							this.onApplyFilter();
							this.cd.detectChanges();
						});
					});
				} else {
					this.filterProvider.storeViews(name, conditions, false, [], () => {
						this.filterProvider.applyFilter(name);
					});
				}
			} else {
				this.currentFilterName = null;
				this.storage.removeSync('currentFilter');
			}
		});
	}

	clearFilter(): void {
		this.currentFilterName = null;
		this.filterProvider.resetFilter();
	}

	applyProjectPointsFilter(): void {
		this.currentFilterName = 'Projektpunkte';
		this.storage.setSync('currentFilter', 'Projektpunkte');
		this.filterProvider.setFilteredDocIds(this.pps.allProjectPointIds());
		this.onApplyFilter();
		this.cd.detectChanges();
	}

	// --- ListFilterMode ---

	setListFilterMode(mode: ListFilterMode): void {
		this.listFilterMode = mode;
		this.boundsFilterActive = (mode === 'map');
		this.storage.setSync('listFilterMode', mode);
		if (this.currentFilterName) {
			this.applyQuickFilter(this.currentFilterName);
		} else {
			this.invalidateSortCache();
			this.rebuildSortCache();
			this.cd.detectChanges();
		}
	}

	// --- Auswahl ---

	addVisibleToSelection(): void {
		if (!this.bps) return;
		const ids: string[] = [];
		this.bps.forEach((bp, i) => {
			if (this.isVisibleInList(i)) {
				ids.push(bp._id);
			}
		});
		this.selectionService.add(ids);
	}

	getVisibleSelectedCount(): number {
		if (!this.bps) return 0;
		let count = 0;
		this.bps.forEach((bp, i) => {
			if (this.isVisibleInList(i) && this.selectionService.isSelected(bp._id)) {
				count++;
			}
		});
		return count;
	}

	removeVisibleFromSelection(): void {
		if (!this.bps) return;
		const ids: string[] = [];
		this.bps.forEach((bp, i) => {
			if (this.isVisibleInList(i) && this.selectionService.isSelected(bp._id)) {
				ids.push(bp._id);
			}
		});
		this.selectionService.removeMultiple(ids);
	}

	invertVisibleSelection(): void {
		if (!this.bps) return;
		const toAdd: string[] = [];
		const toRemove: string[] = [];
		this.bps.forEach((bp, i) => {
			if (this.isVisibleInList(i)) {
				if (this.selectionService.isSelected(bp._id)) {
					toRemove.push(bp._id);
				} else {
					toAdd.push(bp._id);
				}
			}
		});
		this.selectionService.removeMultiple(toRemove);
		this.selectionService.add(toAdd);
	}

	clearSelection(): void {
		this.selectionService.clear();
	}

	// --- Sortierung ---

	toggleListSort(mode: 'selected' | 'filtered'): void {
		this.listSortMode = this.listSortMode === mode ? 'none' : mode;
		this.invalidateSortCache();
		this.rebuildSortCache();
	}

	invalidateSortCache(): void {
		this.sortCacheValid = false;
	}

	rebuildSortCache(): void {
		if (this.sortCacheValid) return;
		if (!this.bps) {
			this.sortedBpsCache = [];
			return;
		}

		const items = this.bps.map((bp, i) => ({ bp, originalIndex: i }));

		if (this.listSortMode !== 'none') {
			items.sort((a, b) => {
				if (this.listSortMode === 'selected') {
					const aSelected = this.selectionService.isSelected(a.bp._id) ? 1 : 0;
					const bSelected = this.selectionService.isSelected(b.bp._id) ? 1 : 0;
					return bSelected - aSelected;
				} else if (this.listSortMode === 'filtered') {
					const aFiltered = this.pointsFilter?.[a.originalIndex] ? 1 : 0;
					const bFiltered = this.pointsFilter?.[b.originalIndex] ? 1 : 0;
					return bFiltered - aFiltered;
				}
				return 0;
			});
		}

		this.sortedBpsCache = items;
		this.sortCacheValid = true;
	}

	// --- Navigation ---

	/** Punkt auf der Karte zentrieren */
	select(bp: T): void {
		const latLngPoint = this.getPointLatLng(bp);
		if (latLngPoint) {
			this.mapStatusService.panTo(latLngPoint, 17);
		}
	}

	// --- Filter-Editor ---

	openFilterEditor(): void {
		if (!this.filterComponentType) return;
		const modalRef = this.modalService.open(this.filterComponentType, { size: 'lg' });
		modalRef.componentInstance.local = !this.workOffline;
		modalRef.componentInstance.filterRect = this.currentBounds;
		modalRef.componentInstance.filterApplied.subscribe((filterName: string) => {
			this.applyQuickFilter(filterName);
		});
	}

	// --- Utilities ---

	trackByBpId(index: number, item: { bp: T; originalIndex: number }): string {
		return index + '_' + item.bp._id;
	}

	async cleanUp(): Promise<void> {
		await this.storage.cleanUp();
		this.msg.success('Der lokale Speicher wurde bereinigt.');
	}
}
